/**
 * AppServiceProviders
 *
 * 统一的服务提供者组件，组合所有核心服务上下文
 * 替代旧的ServicesContext，按照monorepo最佳实践实现服务依赖注入
 *
 * 重要提示:
 * 对于文档ID相关功能，请参阅文档ID生命周期指南：
 * @see ../../packages/core/src/document/DOCUMENT_ID_LIFECYCLE.md
 */

import React, { ReactNode, useCallback, useMemo } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';

// 导入所有服务提供者
import {
  LoggingProvider,
  ErrorProvider,
  DocumentProvider,
  DocumentIdentityProvider,
  LearningProvider,
  TranslationProvider as CoreTranslationProvider,
  HistoryProvider as CoreHistoryProvider,
  useErrorHandler,
  getTranslationStorageService,
} from '@readsavor/core';
import { AuthProvider, useAuthStore } from '@readsavor/auth';
import { shouldTriggerLogout } from '@readsavor/auth';
import { UserInitializationOrchestrator } from '@/components/UserInitializationOrchestrator';
// 导入统一的业务API提供者
import { AppBusinessProvider } from './AppBusinessProvider.js';

// 导入Web特定的翻译提供者
import { WebTranslationProvider } from '../features/translation/contexts/WebTranslationProvider.js';
import { WebTranslationHistoryProvider } from '../features/translation/contexts/WebTranslationHistoryProvider.js';
import { AppError } from '@readsavor/types';

// QueryClient 实例
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: error => {
      if (shouldTriggerLogout(error)) {
        console.warn('[QueryCache] Triggering logout due to auth error:', error);
        window.dispatchEvent(
          new CustomEvent('auth:unauthorized', {
            detail: { error, source: 'query' },
          })
        );
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: error => {
      if (shouldTriggerLogout(error)) {
        console.warn('[MutationCache] Triggering logout due to auth error:', error);
        window.dispatchEvent(
          new CustomEvent('auth:unauthorized', {
            detail: { error, source: 'mutation' },
          })
        );
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error: unknown) => {
        // 认证相关错误不重试
        if (shouldTriggerLogout(error)) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

export const QueryClientContext = React.createContext<QueryClient>(queryClient);

/**
 * 应用服务提供者属性
 */
interface AppServiceProvidersProps {
  children: ReactNode;
}

/**
 * 内部包装器组件
 * 用于访问错误上下文并将其传递给WebTranslationProvider
 */
const WebTranslationWithErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const errorHandler = useErrorHandler();

  const handleTranslationError = useCallback(
    (error: AppError) => {
      errorHandler.handleError(error.message, error);
    },
    [errorHandler]
  );

  return (
    <WebTranslationProvider onTranslationError={handleTranslationError}>
      {children}
    </WebTranslationProvider>
  );
};

/**
 * The main provider component that composes all other providers.
 */
const AppServices: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userId = useAuthStore(state => state.session?.user?.id);

  // 当 userId 变化时 (从 undefined 到一个字符串，或反之),
  // useMemo 会重新计算，从而创建一个新的、与当前用户状态匹配的服务实例。
  const translationStorageService = useMemo(() => getTranslationStorageService(), [userId]);

  // 利用 React 的 key 属性，在用户认证状态变化时强制重新挂载整个业务逻辑相关的 Provider 树。
  // 这确保了所有依赖于用户状态的单例或上下文都能被销毁并以正确的状态重建。
  // 这是解决“僵尸服务实例”问题的最彻底、最符合 React 范式的方法。
  const providersKey = userId || 'anonymous';

  return (
    <AuthProvider>
      <UserInitializationOrchestrator />
      <AppBusinessProvider key={providersKey}>
        <LoggingProvider>
          <ErrorProvider>
            <DocumentIdentityProvider>
              <DocumentProvider>
                <LearningProvider>
                  <CoreTranslationProvider>
                    <CoreHistoryProvider translationService={translationStorageService}>
                      <WebTranslationHistoryProvider>
                        <WebTranslationWithErrorBoundary>
                          {children}
                        </WebTranslationWithErrorBoundary>
                      </WebTranslationHistoryProvider>
                    </CoreHistoryProvider>
                  </CoreTranslationProvider>
                </LearningProvider>
              </DocumentProvider>
            </DocumentIdentityProvider>
          </ErrorProvider>
        </LoggingProvider>
      </AppBusinessProvider>
    </AuthProvider>
  );
};

/**
 * 应用服务提供者组件
 *
 * 顶层提供者，仅负责设置 QueryClientProvider。
 *
 * @param props 组件属性
 * @returns 组合了所有服务提供者的组件
 */
export const AppServiceProviders: React.FC<AppServiceProvidersProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppServices>{children}</AppServices>
    </QueryClientProvider>
  );
};

/**
 * 默认导出应用服务提供者组件
 */
export default AppServiceProviders;
